import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Input,
  Pagination,
  Progress,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { fetchQCAgents, fetchQCAuditDetail, fetchQCAudits, fetchQCMetrics } from "../../apis/call_intelligence.api";
import { CallPlayback, PlaybackFocus, SENTIMENT_WORD, sentimentTag } from "./customer_intelligence/shared";

const { Text, Paragraph } = Typography;

const FLAG_COLOR: Record<string, string> = { red: "red", yellow: "orange", green: "green" };
const FLAG_EMOJI: Record<string, string> = { red: "🔴", yellow: "🟡", green: "🟢" };
const ROLES = [
  { value: "presales", label: "Presales" },
  { value: "design_consultant", label: "Design Consultant" },
  { value: "customer_success", label: "Customer Success" },
  { value: "ai_bot", label: "AI Bot (Superdash)" },
];
const ROLE_LABEL: Record<string, string> = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
const SHORT_CALL_SECONDS = 60;
const TOP_METRICS = 6;
const FLAGS = [
  { key: "red", label: "Red", hint: "Mandatory coaching", bar: "#ff4d4f" },
  { key: "yellow", label: "Yellow", hint: "Targeted coaching", bar: "#faad14" },
  { key: "green", label: "Green", hint: "Meets or exceeds", bar: "#52c41a" },
];

const flagTag = (flag?: string) =>
  flag ? (
    <Tag color={FLAG_COLOR[flag]}>
      {FLAG_EMOJI[flag]} {flag.toUpperCase()}
    </Tag>
  ) : (
    <Text type="secondary">—</Text>
  );

const scoreColor = (n: number) => (n < 2.5 ? "red" : n <= 3.5 ? "orange" : "green");
const pointsColor = (n: number) => (n >= 1 ? "green" : n > 0 ? "orange" : "red");
const SENTIMENT_COLOR: Record<string, string> = { green: "green", amber: "orange", red: "red" };
const pct = (n: number, d: number) => (d ? Math.round((n / d) * 100) : 0);
const dur = (s?: number | null) => (s == null ? "-" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
// every rubric averages on the same 1–5 axis, so 1 → 0% and 5 → 100%
const avgPct = (avg: number) => Math.round(((avg - 1) / 4) * 100);
const callPct = (r: any): number | null =>
  r.score_pct != null ? r.score_pct : r.average_score != null ? avgPct(r.average_score) : null;
const SENTIMENTS = Object.entries(SENTIMENT_WORD).map(([value, label]) => ({
  value,
  label: label.charAt(0).toUpperCase() + label.slice(1),
}));

type Filters = {
  range: [Dayjs, Dayjs];
  role?: string;
  sentiment?: string;
  hideShort: boolean;
  search: string;
  // id = linked user; agents MyOperator knows but who have no user are matched by name
  agent?: { id?: string; name: string };
};

const toParams = (f: Filters) => ({
  start_date: f.range[0].startOf("day").toISOString(),
  end_date: f.range[1].endOf("day").toISOString(),
  agent_role: f.role,
  sentiment: f.sentiment,
  min_duration: f.hideShort ? SHORT_CALL_SECONDS : undefined,
  search: f.search || undefined,
  agent_user: f.agent?.id,
  agent_name: f.agent && !f.agent.id ? f.agent.name : undefined,
});

// Clickable "▶ mm:ss" links that play the recording from that moment.
const Stamps = ({ times, onPlay }: { times?: string[]; onPlay: (f: PlaybackFocus) => void }) =>
  times?.length ? (
    <div>
      {times.map((t) => (
        <Button
          key={t}
          type="link"
          size="small"
          title="Play from here"
          onClick={() => onPlay({ time: t, at: Date.now() })}
          style={{ padding: "0 6px 0 0", height: "auto", fontSize: 12 }}
        >
          ▶ {t}
        </Button>
      ))}
    </div>
  ) : null;

// ---- Flag counts + what fails most, for the current filters ----
const Overview = ({ params, flag, onFlag }: { params: any; flag?: string; onFlag: (f?: string) => void }) => {
  const [kpis, setKpis] = useState<any>(null);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchQCAudits({ ...params, page_size: 1 }), fetchQCMetrics(params)]).then(([a, m]) => {
      setKpis(a?.kpis || null);
      setMetrics(m?.results || []);
      setLoading(false);
    });
  }, [params]);

  const [showAll, setShowAll] = useState(false);
  const audited = kpis?.audited || 0;
  const shown = showAll ? metrics : metrics.slice(0, TOP_METRICS);
  const failColor = (r: number) => (r >= 0.4 ? "#ff4d4f" : r >= 0.2 ? "#faad14" : "#52c41a");

  return (
    <Spin spinning={loading}>
      <Card size="small" style={{ marginBottom: 12 }} bodyStyle={{ padding: "10px 14px" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 16px" }}>
          <span>
            <Text strong style={{ fontSize: 18 }}>
              {audited}
            </Text>{" "}
            <Text type="secondary">calls audited</Text>{" "}
            {kpis?.avg_score != null && (
              <Tag color={scoreColor(kpis.avg_score)}>avg score {avgPct(kpis.avg_score)}%</Tag>
            )}
          </span>
          {FLAGS.map(({ key, label, hint }) => (
            <Tooltip key={key} title={`${hint} — click to ${flag === key ? "clear" : "filter"}`}>
              <Tag.CheckableTag
                checked={flag === key}
                onChange={() => onFlag(flag === key ? undefined : key)}
                style={{
                  fontSize: 13,
                  padding: "2px 8px",
                  border: `1px solid ${FLAG_COLOR[key] === "orange" ? "#ffd591" : "#f0f0f0"}`,
                }}
              >
                {FLAG_EMOJI[key]} {label} <b>{kpis?.[key] ?? "-"}</b> · {pct(kpis?.[key] || 0, audited)}%
              </Tag.CheckableTag>
            </Tooltip>
          ))}
        </div>
        {audited > 0 && (
          <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", margin: "8px 0 4px" }}>
            {FLAGS.map(({ key, bar }) => (
              <div key={key} style={{ width: `${pct(kpis?.[key] || 0, audited)}%`, background: bar }} />
            ))}
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          <Text type="secondary" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 }}>
            What fails most{" "}
            <Tooltip title="Failed = a 1–5 metric scored 2 or below, a yes/no metric answered no, or a 1/0.5/0 customer-success rubric scored 0; N/A is left out. Any critical metric failing makes the call red. (v1) metrics come from audits on the older rubric.">
              <span style={{ cursor: "help" }}>ⓘ</span>
            </Tooltip>
          </Text>
          {metrics.length ? (
            <Row gutter={[24, 0]} style={{ marginTop: 4 }}>
              {shown.map((m) => (
                <Col xs={24} md={12} xl={8} key={m.key}>
                  <Tooltip title={`${m.failed} of ${m.audited} audited calls failed`} placement="topLeft">
                    <div style={{ display: "flex", alignItems: "center", gap: 8, height: 24, fontSize: 13 }}>
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: m.label.includes("(v1)") ? "#8c8c8c" : undefined,
                        }}
                      >
                        {m.critical && (
                          <span style={{ color: "#ff4d4f" }} title="critical">
                            ●{" "}
                          </span>
                        )}
                        {m.label}
                      </span>
                      <div style={{ width: 70, height: 5, borderRadius: 3, background: "#f0f0f0", flex: "none" }}>
                        <div
                          style={{
                            width: `${Math.round(m.fail_rate * 100)}%`,
                            height: "100%",
                            borderRadius: 3,
                            background: failColor(m.fail_rate),
                          }}
                        />
                      </div>
                      <span style={{ width: 34, textAlign: "right", fontVariantNumeric: "tabular-nums", flex: "none" }}>
                        {Math.round(m.fail_rate * 100)}%
                      </span>
                    </div>
                  </Tooltip>
                </Col>
              ))}
            </Row>
          ) : (
            <div>
              <Text type="secondary">No audits for these filters.</Text>
            </div>
          )}
          {metrics.length > TOP_METRICS && (
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setShowAll(!showAll)}>
              {showAll ? "Show fewer" : `Show all ${metrics.length} metrics`}
            </Button>
          )}
          {metrics.some((m) => m.critical) && (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
              <span style={{ color: "#ff4d4f" }}>●</span> critical — failing it makes the call red
            </Text>
          )}
        </div>
      </Card>
    </Spin>
  );
};

// ---- Per-call audits ----
const AuditsView = ({ params, flag }: { params: any; flag?: string }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [count, setCount] = useState(0);
  const [ordering, setOrdering] = useState("-call__call_start_time");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [focus, setFocus] = useState<PlaybackFocus>();
  const listReq = useRef(0);
  const detailReq = useRef(0);

  const load = (p: number, size: number) => {
    const req = ++listReq.current;
    setLoading(true);
    fetchQCAudits({ ...params, flag, ordering, page: p, page_size: size }).then((res) => {
      if (req !== listReq.current) return;
      setData(res?.results || []);
      setCount(res?.count || 0);
      setLoading(false);
    });
  };

  useEffect(() => {
    setPage(1);
    load(1, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, flag, ordering]);

  const openDetail = (id: number) => {
    const req = ++detailReq.current;
    setOpenId(id);
    setDrawerOpen(true);
    setDetail(null);
    setFocus(undefined);
    setDetailLoading(true);
    fetchQCAuditDetail(id).then((res) => {
      if (req !== detailReq.current) return;
      setDetail(res);
      setDetailLoading(false);
    });
  };

  const openIndex = data.findIndex((r) => r.id === openId);
  const step = (delta: number) => {
    const next = data[openIndex + delta];
    if (next) openDetail(next.id);
  };

  const metrics = useMemo(
    () =>
      [...(detail?.metrics || [])].sort(
        (a: any, b: any) => Number(b.failed) - Number(a.failed) || Number(b.critical) - Number(a.critical)
      ),
    [detail]
  );

  const columns: ColumnsType<any> = [
    {
      title: "Date / Time",
      key: "call_start_time",
      width: 150,
      render: (r) => (r.call_start_time ? dayjs(r.call_start_time).format("DD MMM YY, HH:mm") : "-"),
    },
    { title: "Agent", key: "agent", render: (r) => r.agent_name || "-" },
    {
      title: "Role",
      key: "agent_role",
      width: 150,
      render: (r) => ROLE_LABEL[r.agent_role] || r.agent_role,
    },
    {
      title: "Customer",
      key: "customer",
      render: (r) => r.customer_name || "-",
    },
    {
      title: "Summary",
      key: "summary",
      width: 280,
      render: (r) => {
        const text = r.summary || r.call_summary;
        return text ? (
          <Paragraph type="secondary" ellipsis={{ rows: 2, tooltip: text }} style={{ marginBottom: 0, fontSize: 12 }}>
            {text}
          </Paragraph>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Duration",
      key: "duration",
      width: 90,
      render: (r) => (
        <span>
          {dur(r.duration)}{" "}
          {r.duration != null && r.duration < SHORT_CALL_SECONDS && <Tag style={{ marginLeft: 4 }}>short</Tag>}
        </span>
      ),
    },
    {
      title: "QC score",
      key: "score",
      width: 90,
      render: (r) => {
        const score = callPct(r);
        return score != null ? <Tag color={scoreColor(r.average_score)}>{score}%</Tag> : "-";
      },
    },
    { title: "Flag", key: "flag", width: 110, render: (r) => flagTag(r.flag) },
    {
      title: "Sentiment",
      key: "sentiment",
      width: 130,
      render: (r) => (r.sentiment ? sentimentTag(r.sentiment, r.severity) : <Text type="secondary">—</Text>),
    },
    {
      title: "",
      key: "action",
      width: 80,
      render: () => <Text style={{ color: "#1677ff" }}>Details</Text>,
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 8 }}>
        <Text type="secondary">Sort</Text>
        <Select
          size="small"
          style={{ width: 200 }}
          value={ordering}
          onChange={setOrdering}
          options={[
            { value: "-call__call_start_time", label: "Newest call first" },
            { value: "average_score", label: "Lowest score first" },
            { value: "-average_score", label: "Highest score first" },
            { value: "-call__composite_score", label: "Most negative sentiment first" },
          ]}
        />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
        size="middle"
        scroll={{ x: 1300 }}
        onRow={(r) => ({
          onClick: () => openDetail(r.id),
          style: { cursor: "pointer", background: r.id === openId && drawerOpen ? "#e6f4ff" : undefined },
        })}
      />

      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={count}
          showSizeChanger
          showTotal={(t) => `${t} audits`}
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
            load(p, size);
          }}
        />
      </div>

      <Drawer
        title={detail ? `Call ${detail.kylas_call_log_id}` : "Call"}
        width={1180}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Space>
            <Button size="small" disabled={openIndex <= 0} onClick={() => step(-1)}>
              ‹ Prev
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {openIndex >= 0 ? `${openIndex + 1} of ${data.length} on this page` : ""}
            </Text>
            <Button size="small" disabled={openIndex < 0 || openIndex >= data.length - 1} onClick={() => step(1)}>
              Next ›
            </Button>
          </Space>
        }
      >
        {detailLoading && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        )}
        {detail && (
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="Agent">{detail.agent_name || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Role">
                    {ROLE_LABEL[detail.agent_role] || detail.agent_role || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Customer">{detail.customer_name || "-"}</Descriptions.Item>
                  <Descriptions.Item label="Call">
                    {detail.call_start_time ? dayjs(detail.call_start_time).format("DD MMM YY, HH:mm") : "-"} ·{" "}
                    {detail.call_type === "incoming" ? "Inbound" : "Outbound"} · {dur(detail.duration)}
                  </Descriptions.Item>
                  <Descriptions.Item label="QC score">
                    {callPct(detail) != null ? `${callPct(detail)}%` : "—"} {flagTag(detail.flag)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Sentiment">
                    {detail.sentiment ? sentimentTag(detail.sentiment, detail.severity) : "—"}
                  </Descriptions.Item>
                </Descriptions>

                {(detail.summary || detail.call_summary) && (
                  <div>
                    <Text strong>Summary</Text>
                    {detail.summary && <Paragraph style={{ marginTop: 6, marginBottom: 4 }}>{detail.summary}</Paragraph>}
                    {detail.call_summary && <Paragraph style={{ marginTop: 6 }}>{detail.call_summary}</Paragraph>}
                  </div>
                )}

                {detail.sentiment && (
                  <Card size="small" title="Overall sentiment" extra={sentimentTag(detail.sentiment, detail.severity)}>
                    {detail.sentiment_reasoning && <Text>{detail.sentiment_reasoning}</Text>}
                    {detail.matched_keywords?.length > 0 && (
                      <div style={{ marginTop: 6 }}>
                        {detail.matched_keywords.map((k: string) => (
                          <Tag key={k} color="red">
                            {k}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </Card>
                )}

                {detail.duration != null && detail.duration < SHORT_CALL_SECONDS && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Short call"
                    description="Calls under a minute are often callbacks or 'call me later' — greeting and closing scores may not be meaningful."
                  />
                )}

                {detail.customer_sentiment && (
                  <Card
                    size="small"
                    title="Customer sentiment"
                    extra={<Text type="secondary">not in agent score</Text>}
                  >
                    {detail.customer_sentiment.score === "na" ? (
                      <Tag>Unassessable</Tag>
                    ) : (
                      <Tag color={SENTIMENT_COLOR[detail.customer_sentiment.score]}>
                        {String(detail.customer_sentiment.score).toUpperCase()}
                      </Tag>
                    )}
                    <Text>{detail.customer_sentiment.note}</Text>
                    <Stamps times={detail.customer_sentiment.timestamps} onPlay={setFocus} />
                  </Card>
                )}

                <div>
                  <Text strong>Rubric</Text> <Text type="secondary">— failing metrics first</Text>
                  <Table
                    rowKey="key"
                    style={{ marginTop: 8 }}
                    size="small"
                    pagination={false}
                    dataSource={metrics}
                    onRow={(m: any) => ({
                      style: m.failed ? { background: "#fff1f0" } : {},
                    })}
                    columns={[
                      {
                        title: "Metric",
                        key: "label",
                        render: (m: any) => (
                          <span>
                            {m.label} {m.critical && <Tag color="red">critical</Tag>}
                          </span>
                        ),
                      },
                      {
                        title: "Score",
                        key: "score",
                        width: 90,
                        render: (m: any) =>
                          m.score === "na" ? (
                            <Tag>N/A</Tag>
                          ) : m.kind === "points" ? (
                            <Tag color={pointsColor(Number(m.score))}>{m.score} / 1</Tag>
                          ) : m.kind === "binary" ? (
                            <Tag color={m.score === "yes" ? "green" : "red"}>{String(m.score).toUpperCase()}</Tag>
                          ) : (
                            <Tag color={scoreColor(Number(m.score))}>{m.score}</Tag>
                          ),
                      },
                      {
                        title: "Note and evidence",
                        key: "note",
                        render: (m: any) => (
                          <div>
                            {m.note}
                            {m.evidence && (
                              <div>
                                <Button
                                  type="link"
                                  size="small"
                                  title="Show in transcript"
                                  onClick={() =>
                                    setFocus({
                                      quote: m.evidence,
                                      at: Date.now(),
                                    })
                                  }
                                  style={{
                                    padding: 0,
                                    height: "auto",
                                    whiteSpace: "normal",
                                    textAlign: "left",
                                    fontSize: 12,
                                    fontStyle: "italic",
                                    color: "#8c8c8c",
                                  }}
                                >
                                  “{m.evidence}”{" "}
                                  <span style={{ fontStyle: "normal", color: "#1677ff" }}>↗ in transcript</span>
                                </Button>
                              </div>
                            )}
                            <Stamps times={m.timestamps} onPlay={setFocus} />
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>

              </Space>
            </Col>
            <Col xs={24} lg={12}>
              <div style={{ position: "sticky", top: 0 }}>
                <Text strong>Recording & transcript</Text>
                <div style={{ marginTop: 6 }}>
                  <CallPlayback
                    key={detail.id}
                    callId={detail.call_id}
                    fallbackUrl={detail.recording_url}
                    lines={detail.transcript_lines}
                    raw={detail.transcript}
                    duration={detail.duration}
                    focus={focus}
                    maxHeight={560}
                  />
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Drawer>
    </>
  );
};

// ---- Per-agent rollup ----
const AgentsView = ({
  params,
  onOpenAgent,
}: {
  params: any;
  onOpenAgent: (id: string | undefined, name: string) => void;
}) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const load = (p: number) => {
    setLoading(true);
    fetchQCAgents({ ...params, page: p, page_size: 20 }).then((res) => {
      setData(res?.results || []);
      setCount(res?.count || 0);
      setLoading(false);
    });
  };
  useEffect(() => {
    setPage(1);
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const columns: ColumnsType<any> = [
    {
      title: "Agent",
      key: "agent",
      render: (r) => (
        <span>
          <Text style={{ color: "#1677ff" }}>{r.agent_name || r.agent_user}</Text>
          {!r.agent_user && (
            <Tag style={{ marginLeft: 6 }} title="MyOperator agent not mapped to a user">
              not mapped
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "Role",
      key: "agent_role",
      width: 170,
      render: (r) => ROLE_LABEL[r.agent_role] || r.agent_role,
    },
    { title: "Audited", dataIndex: "audited", key: "audited", width: 90 },
    {
      title: "Avg score",
      key: "avg_score",
      width: 100,
      render: (r) => (r.avg_score != null ? <Tag color={scoreColor(r.avg_score)}>{avgPct(r.avg_score)}%</Tag> : "-"),
    },
    {
      title: "Red rate",
      key: "red_rate",
      width: 160,
      render: (r) => (
        <Progress
          percent={Math.round((r.red_rate || 0) * 100)}
          size="small"
          status={r.red_rate >= 0.4 ? "exception" : "normal"}
        />
      ),
    },
    { title: "🔴", dataIndex: "red", key: "red", width: 60 },
    { title: "🟡", dataIndex: "yellow", key: "yellow", width: 60 },
    { title: "🟢", dataIndex: "green", key: "green", width: 60 },
  ];

  return (
    <>
      <Table
        rowKey={(r) => `${r.agent_user || r.agent_name}-${r.agent_role}`}
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
        size="middle"
        onRow={(r) => ({
          onClick: () => onOpenAgent(r.agent_user || undefined, r.agent_name || r.agent_user),
          style: { cursor: "pointer" },
        })}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={20}
          total={count}
          showTotal={(t) => `${t} agents · click one to see their calls`}
          onChange={(p) => {
            setPage(p);
            load(p);
          }}
        />
      </div>
    </>
  );
};

const QCTab = () => {
  const [view, setView] = useState<string | number>("calls");
  const [flag, setFlag] = useState<string>();
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState<Filters>({
    range: [dayjs().subtract(30, "day"), dayjs()],
    hideShort: false,
    search: "",
  });
  const params = useMemo(() => toParams(filters), [filters]);
  const set = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: "By Call", value: "calls" },
            { label: "By Agent", value: "agents" },
          ]}
        />
        <DatePicker.RangePicker
          allowClear={false}
          value={filters.range}
          onChange={(v) => v?.[0] && v?.[1] && set({ range: [v[0], v[1]] })}
          presets={[
            {
              label: "Last 7 days",
              value: [dayjs().subtract(7, "day"), dayjs()],
            },
            {
              label: "Last 30 days",
              value: [dayjs().subtract(30, "day"), dayjs()],
            },
            {
              label: "Last 90 days",
              value: [dayjs().subtract(90, "day"), dayjs()],
            },
          ]}
        />
        <Select
          placeholder="All roles"
          allowClear
          style={{ width: 180 }}
          value={filters.role}
          onChange={(role) => set({ role })}
          options={ROLES}
        />
        <Select
          placeholder="Any sentiment"
          allowClear
          style={{ width: 150 }}
          value={filters.sentiment}
          onChange={(sentiment) => set({ sentiment })}
          options={SENTIMENTS}
        />
        <Input.Search
          placeholder="Agent / customer"
          allowClear
          style={{ width: 220 }}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            if (!e.target.value) set({ search: "" });
          }}
          onSearch={(v) => set({ search: v })}
        />
        <Space size={6}>
          <Switch size="small" checked={filters.hideShort} onChange={(hideShort) => set({ hideShort })} />
          <Text>Hide calls under 1 min</Text>
        </Space>
        {filters.agent && (
          <Tag closable color="blue" onClose={() => set({ agent: undefined })}>
            Agent: {filters.agent.name}
          </Tag>
        )}
        {flag && (
          <Button size="small" onClick={() => setFlag(undefined)}>
            Clear flag filter
          </Button>
        )}
      </Space>

      <Overview
        params={params}
        flag={flag}
        onFlag={(f) => {
          setFlag(f);
          setView("calls");
        }}
      />

      {view === "calls" ? (
        <AuditsView params={params} flag={flag} />
      ) : (
        <AgentsView
          params={params}
          onOpenAgent={(id, name) => {
            set({ agent: { id, name } });
            setView("calls");
          }}
        />
      )}
    </div>
  );
};

export default QCTab;
